import { njAreas } from "./areas/nj";
import { nyAreas } from "./areas/ny";
import { paAreas } from "./areas/pa";

export interface CityAirportTime {
  code: string;
  label: string;
  time: string;
}

export interface CityTrip {
  label: string;
  path: string;
  blurb: string;
}

export interface CityFaq {
  q: string;
  a: string;
}

export interface ServiceArea {
  slug: string;
  city: string;
  state: "NJ" | "PA" | "NY";
  county: string;
  title: string;
  description: string;
  h1: string;
  tagline: string;
  intro: string[];
  airports: CityAirportTime[];
  landmarks: string[];
  neighborhoods: string[];
  popular: CityTrip[];
  faqs: CityFaq[];
  nearby: string[];
}

// Airport pages are shared across every city page so the internal links always
// point at the canonical airport route.
const AIRPORT_PATHS: Record<string, string> = {
  PHL: "/services/airport-transportation/phl",
  EWR: "/services/airport-transportation/ewr",
  JFK: "/services/airport-transportation/jfk",
  LGA: "/services/airport-transportation/lga",
  ACY: "/services/airport-transportation/acy",
};

export const airportPath = (code: string): string =>
  AIRPORT_PATHS[code] || "/airports";

const southJerseyAreas: ServiceArea[] = [
  {
    slug: "glassboro-nj",
    city: "Glassboro",
    state: "NJ",
    county: "Gloucester County",
    title: "Glassboro Limo Service & Airport Car Service",
    description:
      "Chauffeur-driven limo and black car service in Glassboro, NJ. Airport transfers to PHL, EWR and JFK, weddings, proms and corporate rides. Call (929) 452-6031.",
    h1: "Limo Service in Glassboro, NJ",
    tagline:
      "Our home base. Chauffeured sedans, SUVs and Sprinter vans for Rowan events, airport runs and everything in between.",
    intro: [
      "GS Limo Elite is based on Delsea Drive in Glassboro, which means a chauffeured vehicle can usually reach your door faster here than anywhere else we serve. From Rowan University move-in weekends and commencement to early-morning flights out of PHL, we handle the drive so you can focus on the reason for the trip.",
      "Glassboro sits minutes from Route 55 and Route 47, giving our chauffeurs a straight shot to Philadelphia International, the New Jersey Turnpike and the Atlantic City Expressway. Whether you need a one-way airport transfer or a vehicle on standby for the day, every ride is quoted and confirmed before you travel.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 30–40 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 50–65 min" },
      { code: "JFK", label: "JFK International", time: "About 75–95 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 50–60 min" },
    ],
    landmarks: [
      "Rowan University campus and Rowan Boulevard",
      "Glassboro Town Square",
      "Heritage Glass Museum",
      "Delsea Drive (Route 47) business corridor",
      "South Jersey Technology Park",
      "Route 55 and Route 322 connections",
    ],
    neighborhoods: [
      "Downtown Glassboro",
      "Rowan University area",
      "Aura",
      "Ferrell",
      "Pitman border",
      "Elk Township border",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "Flight-tracked pickups to PHL, EWR, JFK and ACY, with pickup times planned around your terminal and airline.",
      },
      {
        label: "Wedding transportation",
        path: "/services/weddings",
        blurb:
          "Bridal party and guest shuttles for Glassboro and Rowan-area venues, coordinated around your timeline.",
      },
      {
        label: "Prom and school events",
        path: "/services/prom",
        blurb:
          "Group transportation for Glassboro and Rowan-area students, with pickup and return times agreed up front.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Point-to-point rides for meetings, client pickups and business travel across South Jersey and Philadelphia.",
      },
    ],
    faqs: [
      {
        q: "Do you provide transportation for Rowan University events?",
        a: "Yes. We regularly handle move-in and move-out trips, parents weekends, formals and commencement visits. Share your dates and group size and we will confirm vehicle availability for each leg.",
      },
      {
        q: "How long does it take to get from Glassboro to PHL?",
        a: "Typically 30 to 40 minutes depending on traffic and the time of day. We plan the pickup time backwards from your flight so you arrive with time to spare.",
      },
      {
        q: "Can you pick up at Glassboro Town Square or Rowan Boulevard?",
        a: "Yes. We confirm a precise curbside pickup point with you before the trip, including for crowded event nights when some streets are closed.",
      },
      {
        q: "Do you offer hourly service in Glassboro?",
        a: "Yes. Hourly and as-directed service is available when you need a vehicle on standby for multiple stops, such as a wedding day or a full day of meetings.",
      },
    ],
    nearby: ["pitman-nj", "mullica-hill-nj", "sewell-washington-township-nj", "deptford-nj"],
  },
  {
    slug: "mullica-hill-nj",
    city: "Mullica Hill",
    state: "NJ",
    county: "Gloucester County",
    title: "Mullica Hill Limo & Executive Car Service",
    description:
      "Limo and executive car service in Mullica Hill and Harrison Township, NJ. Airport transfers, weddings, winery tours and corporate rides. Call (929) 452-6031.",
    h1: "Limo Service in Mullica Hill, NJ",
    tagline:
      "Quiet, chauffeur-driven rides from historic Main Street to PHL, EWR and your event venue.",
    intro: [
      "Mullica Hill pairs a historic Main Street with quick access to Route 322 and Route 55, so a chauffeured car is a practical option for airport runs, weddings and a day of antique shopping. We serve the village center, Richwood and the surrounding Harrison Township neighborhoods.",
      "For travelers, the appeal is simple: your chauffeur handles the Turnpike and airport traffic while you keep your schedule. For celebrations, it means the whole party arrives together and gets home without splitting into separate cars.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 30–40 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 45–60 min" },
      { code: "JFK", label: "JFK International", time: "About 75–90 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 45–55 min" },
    ],
    landmarks: [
      "Historic Main Street and antique shops",
      "Richwood Academy Cultural Center",
      "Gloucester County 4-H Fairgrounds",
      "Route 322 and Route 55 connections",
      "Harrison Township municipal area",
      "Local wineries and farm markets",
    ],
    neighborhoods: [
      "Mullica Hill village center",
      "Richwood",
      "Harrison Township",
      "Elk Township border",
      "Swedesboro border",
      "Glassboro border",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "Door-to-door service to PHL, EWR, JFK and ACY with flight tracking and a confirmed pickup window.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Bridal party and guest transportation for barn, farm and winery weddings around Harrison Township.",
      },
      {
        label: "Wine and sightseeing",
        path: "/services/wine-and-sightseeing",
        blurb:
          "Hourly chauffeur service for South Jersey wineries and a day out in the countryside.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Executive rides for meetings and client visits along the Route 55 and Route 322 corridors.",
      },
    ],
    faqs: [
      {
        q: "Do you serve Richwood and the rest of Harrison Township?",
        a: "Yes. Mullica Hill, Richwood and the surrounding Harrison Township neighborhoods are all within our regular service area.",
      },
      {
        q: "How far is Mullica Hill from Philadelphia International Airport?",
        a: "Usually 30 to 40 minutes by car, depending on Route 55 and I-95 traffic. We schedule the pickup so you reach the terminal with time for check-in and security.",
      },
      {
        q: "Can you handle a winery or antique-shopping day trip?",
        a: "Yes. Hourly service lets the vehicle stay with you between stops, so you are not arranging separate rides all day.",
      },
      {
        q: "Do you provide wedding transportation in Mullica Hill?",
        a: "Yes. We coordinate bridal party pickups, guest shuttles and the final ride home around your ceremony and reception timeline.",
      },
    ],
    nearby: ["swedesboro-woolwich-nj", "glassboro-nj", "woodbury-nj", "cherry-hill-nj"],
  },
  {
    slug: "swedesboro-woolwich-nj",
    city: "Swedesboro",
    state: "NJ",
    county: "Gloucester County",
    title: "Swedesboro & Woolwich Limo Service",
    description:
      "Limo and black car service in Swedesboro, Woolwich Township and Bennetts Mill, NJ. Airport transfers to PHL and EWR, corporate rides and events. (929) 452-6031.",
    h1: "Limo Service in Swedesboro & Woolwich Township, NJ",
    tagline:
      "Chauffeured airport transfers and event rides for Swedesboro, Woolwich and Bennetts Mill.",
    intro: [
      "Swedesboro and Woolwich Township sit right where Route 322 meets the New Jersey Turnpike, which makes this one of the most convenient places in Gloucester County to start an airport trip. We cover the historic borough, Bennetts Mill and the newer Woolwich developments.",
      "Most of our rides here are straightforward: a flight out of PHL or EWR, a client visit in Philadelphia, or a celebration that needs everyone in one vehicle. Tell us the pickup address and flight details and we will confirm the plan before you book.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 30–40 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 45–60 min" },
      { code: "JFK", label: "JFK International", time: "About 75–90 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 50–60 min" },
    ],
    landmarks: [
      "Swedesboro historic downtown",
      "Swedesboro Lake Park",
      "Bennetts Mill",
      "Route 322 and NJ Turnpike access",
      "Woolwich Township municipal area",
      "Logan Township border",
    ],
    neighborhoods: [
      "Swedesboro borough",
      "Woolwich Township",
      "Bennetts Mill",
      "Logan Township border",
      "Harrison Township border",
      "Oldmans Township border",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "Turnpike-friendly airport service to PHL, EWR, JFK and ACY, with flight tracking built into the schedule.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Reliable business transportation for Woolwich offices, client pickups and Philadelphia meetings.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Bridal party and guest transportation for Swedesboro, Woolwich and Logan Township venues.",
      },
      {
        label: "Group events",
        path: "/services/group-events",
        blurb:
          "Sprinter and group vehicle requests for parties, tournaments and family trips.",
      },
    ],
    faqs: [
      {
        q: "Do you serve Bennetts Mill and Woolwich Township?",
        a: "Yes. Bennetts Mill, Swedesboro borough and the wider Woolwich Township area are all covered.",
      },
      {
        q: "How close is Swedesboro to the New Jersey Turnpike?",
        a: "The Route 322 interchange is minutes away, which keeps airport trips to EWR and the New York airports efficient.",
      },
      {
        q: "How long is the drive to PHL?",
        a: "Typically 30 to 40 minutes. Add time during weekday rush hour, and we will adjust the pickup accordingly.",
      },
      {
        q: "Can you pick up from Logan Township as well?",
        a: "Yes. Logan Township and nearby Woolwich neighborhoods are within our regular service area.",
      },
    ],
    nearby: ["mullica-hill-nj", "west-deptford-nj", "woodbury-nj", "glassboro-nj"],
  },
  {
    slug: "west-deptford-nj",
    city: "West Deptford",
    state: "NJ",
    county: "Gloucester County",
    title: "West Deptford Limo & Airport Car Service",
    description:
      "Limo and airport car service in West Deptford, NJ. Chauffeured rides to PHL, EWR and JFK from Thorofare, Verga and RiverWinds. Call (929) 452-6031.",
    h1: "Limo Service in West Deptford, NJ",
    tagline:
      "Fast access to I-295 and PHL for Thorofare, Verga and RiverWinds clients.",
    intro: [
      "West Deptford is one of the closest Gloucester County townships to Philadelphia International Airport, and I-295 runs right along its edge. That makes a chauffeured airport transfer a genuinely low-stress option, especially for early departures and late arrivals.",
      "We serve Thorofare, Verga, Colonial Manor and the RiverWinds area, plus corporate travel along the I-295 corridor. Our chauffeurs know the terminal approach roads, so you are not guessing where to meet your driver.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 20–30 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 45–60 min" },
      { code: "JFK", label: "JFK International", time: "About 75–90 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 50–60 min" },
    ],
    landmarks: [
      "RiverWinds Community Center and golf course",
      "Delaware River waterfront",
      "I-295 and Route 45 corridors",
      "Thorofare and Verga neighborhoods",
      "Red Bank Battlefield Park nearby",
      "West Deptford municipal area",
    ],
    neighborhoods: [
      "Thorofare",
      "Verga",
      "Colonial Manor",
      "West Deptford Township",
      "Woodbury border",
      "Paulsboro border",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "One of the shortest runs to PHL in Gloucester County, with flight monitoring on every trip.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Point-to-point executive service for offices and plants along the I-295 corridor.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Wedding day transportation for RiverWinds and Delaware River waterfront venues.",
      },
      {
        label: "Night on the town",
        path: "/services/night-on-the-town",
        blurb:
          "Philadelphia restaurants, concerts and sports with a chauffeur handling the drive home.",
      },
    ],
    faqs: [
      {
        q: "Can you pick up at RiverWinds Community Center?",
        a: "Yes. RiverWinds events, races and recreation programs are all within our service area. We confirm the exact pickup point in advance.",
      },
      {
        q: "How long does West Deptford to PHL take?",
        a: "Usually 20 to 30 minutes. It is one of the closest towns we serve to the airport, though bridge and I-95 traffic can add time.",
      },
      {
        q: "Do you provide corporate accounts along I-295?",
        a: "Yes. We handle recurring business travel, client pickups and airport runs for companies in the corridor.",
      },
      {
        q: "Can you take us to a Philadelphia concert or game?",
        a: "Yes. We handle drop-offs and scheduled returns for Philadelphia sports, concerts and theater, so nobody has to drive home late.",
      },
    ],
    nearby: ["woodbury-nj", "deptford-nj", "swedesboro-woolwich-nj", "philadelphia-pa"],
  },
  {
    slug: "woodbury-nj",
    city: "Woodbury",
    state: "NJ",
    county: "Gloucester County",
    title: "Woodbury NJ Limo Service & Airport Transfers",
    description:
      "Chauffeured limo and airport car service in Woodbury, NJ. Transfers to PHL, EWR and JFK, county business travel and event rides. Call (929) 452-6031.",
    h1: "Limo Service in Woodbury, NJ",
    tagline:
      "Chauffeur-driven transportation for Gloucester County's county seat, minutes from PHL.",
    intro: [
      "Woodbury is the Gloucester County seat, so a lot of travel here is tied to the courthouse, county offices and medical appointments. A scheduled chauffeur removes the parking problem and keeps a tight schedule on track.",
      "The borough is also one of the closest points to Philadelphia International Airport in the county. We serve Broad Street, Bell Lake, the West End and surrounding neighborhoods with sedans, SUVs and Sprinters.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 20–30 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 45–60 min" },
      { code: "JFK", label: "JFK International", time: "About 75–90 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 50–60 min" },
    ],
    landmarks: [
      "Downtown Broad Street",
      "Gloucester County Justice Complex",
      "Inspira Medical Center Woodbury",
      "Bell Lake",
      "Route 45 and I-295 access",
      "Woodbury station area",
    ],
    neighborhoods: [
      "Downtown Woodbury",
      "Bell Lake",
      "West End",
      "East Woodbury",
      "West Deptford border",
      "Deptford border",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "Reliable scheduled pickups to PHL, EWR and JFK with enough buffer for court, work or an early flight.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Day-rate and point-to-point service for county business, meetings and client visits.",
      },
      {
        label: "Hourly service",
        path: "/services/hourly-as-directed",
        blurb:
          "Keep a vehicle on standby for multiple appointments, with the chauffeur waiting between stops.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Bridal party transportation for Woodbury and Gloucester County venues.",
      },
    ],
    faqs: [
      {
        q: "Do you handle courthouse and county business travel?",
        a: "Yes. We schedule pickups around court appearances, meetings and appointments so you are not hunting for parking in downtown Woodbury.",
      },
      {
        q: "Can you provide non-emergency medical transportation?",
        a: "We provide private, scheduled car service to appointments and discharges. We are not an ambulance or medical transport service, so please confirm any clinical requirements with your provider.",
      },
      {
        q: "How long is Woodbury to PHL?",
        a: "About 20 to 30 minutes depending on traffic, which makes Woodbury one of the most convenient towns for airport transfers in the county.",
      },
      {
        q: "Is hourly service available in Woodbury?",
        a: "Yes. Hourly and as-directed bookings are available for days with several stops or appointments.",
      },
    ],
    nearby: ["west-deptford-nj", "deptford-nj", "pitman-nj", "glassboro-nj"],
  },
  {
    slug: "pitman-nj",
    city: "Pitman",
    state: "NJ",
    county: "Gloucester County",
    title: "Pitman NJ Limo Service & Airport Car Service",
    description:
      "Limo and airport car service in Pitman, NJ. Chauffeured rides to PHL, EWR and JFK, plus theater nights, weddings and proms. Call (929) 452-6031.",
    h1: "Limo Service in Pitman, NJ",
    tagline:
      "Small-town service with a professional chauffeur — from Uptown to the airport and back.",
    intro: [
      "Pitman's walkable Uptown and the Broadway Theatre make it a town where people go out in the evening, and that is exactly when a chauffeured ride makes sense. No circling for parking on Broadway, no splitting a group across cars.",
      "The borough is also a short run to Route 55 and PHL. We serve Pitman Grove, Alcyon Lake and the surrounding streets with sedans, SUVs and group vehicles for weddings, proms and airport travel.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 25–35 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 50–65 min" },
      { code: "JFK", label: "JFK International", time: "About 80–95 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 50–60 min" },
    ],
    landmarks: [
      "Broadway Theatre of Pitman",
      "Uptown Pitman shops and restaurants",
      "Pitman Grove",
      "Alcyon Lake",
      "Broadway corridor",
      "Route 55 access",
    ],
    neighborhoods: [
      "Uptown Pitman",
      "Pitman Grove",
      "Alcyon Lake area",
      "Broadway corridor",
      "Glassboro border",
      "Washington Township border",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "Scheduled airport pickups and drop-offs at PHL, EWR and JFK with flight tracking.",
      },
      {
        label: "Night on the town",
        path: "/services/night-on-the-town",
        blurb:
          "Dinner, theater and Philadelphia nights with a chauffeur handling the return trip.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Wedding day transportation for Pitman and Gloucester County celebrations.",
      },
      {
        label: "Prom",
        path: "/services/prom",
        blurb:
          "Group prom transportation with agreed pickup and return times for parents' peace of mind.",
      },
    ],
    faqs: [
      {
        q: "Can you drop off and pick up at the Broadway Theatre?",
        a: "Yes. We arrange a curbside drop-off and a confirmed return pickup time so nobody is waiting on Broadway after the show.",
      },
      {
        q: "How long does Pitman to PHL take?",
        a: "Typically 25 to 35 minutes, depending on Route 55 and I-95 traffic.",
      },
      {
        q: "Do you provide prom transportation for Pitman students?",
        a: "Yes. We coordinate group pickups, photo stops and the return time with parents and students in advance.",
      },
      {
        q: "Can you pick up in Pitman Grove?",
        a: "Yes. Pitman Grove and all surrounding Pitman neighborhoods are covered.",
      },
    ],
    nearby: ["glassboro-nj", "sewell-washington-township-nj", "woodbury-nj", "mullica-hill-nj"],
  },
  {
    slug: "sewell-washington-township-nj",
    city: "Sewell",
    state: "NJ",
    county: "Gloucester County",
    title: "Sewell NJ Limo Service & Airport Car Service",
    description:
      "Limo and airport car service in Sewell and Washington Township, NJ. Chauffeured transfers to PHL, EWR and JFK, weddings and group events. (929) 452-6031.",
    h1: "Limo Service in Sewell & Washington Township, NJ",
    tagline:
      "Chauffeured rides for Washington Township, Turnersville and Sewell — airports, weddings and game days.",
    intro: [
      "Sewell covers a lot of ground, from Washington Lake Park to the Route 42 corridor, and a single pickup can save a family from taking two or three cars to the airport. We serve Sewell, Turnersville and the wider Washington Township area.",
      "Route 42 puts PHL and Philadelphia within easy reach, while the Atlantic City Expressway is a straight run east. For local events, our chauffeurs handle the parking and the late-night drive home.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 30–40 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 55–70 min" },
      { code: "JFK", label: "JFK International", time: "About 85–100 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 45–55 min" },
    ],
    landmarks: [
      "Washington Lake Park",
      "Hollydell Ice Arena",
      "Jefferson Health Washington Township",
      "Route 42 corridor",
      "Turnersville shopping district",
      "Washington Township municipal area",
    ],
    neighborhoods: [
      "Sewell",
      "Turnersville",
      "Washington Township",
      "Hurffville",
      "Pitman border",
      "Mantua border",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "Family and group airport runs to PHL, EWR, JFK and ACY with luggage planned into the vehicle choice.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Bridal party and guest transportation for Washington Township venues and nearby farms.",
      },
      {
        label: "Sports events",
        path: "/services/sports-events",
        blurb:
          "Team and family travel to Philadelphia games, tournaments and Hollydell events.",
      },
      {
        label: "Prom",
        path: "/services/prom",
        blurb:
          "Group prom service covering multiple pickups and a confirmed return time.",
      },
    ],
    faqs: [
      {
        q: "Do you serve Turnersville and Hurffville?",
        a: "Yes. Sewell, Turnersville, Hurffville and the rest of Washington Township are all within our service area.",
      },
      {
        q: "Can you handle non-emergency hospital trips to Jefferson Washington Township?",
        a: "Yes. We provide scheduled private car service to appointments and discharges. We are not a medical transport provider, so please confirm any clinical needs with the hospital.",
      },
      {
        q: "How far is Sewell from PHL?",
        a: "Usually 30 to 40 minutes via Route 42 and I-295, depending on traffic.",
      },
      {
        q: "Do you provide group transportation for tournaments?",
        a: "Yes. Tell us the group size, luggage and schedule and we will recommend a Sprinter or multiple vehicles.",
      },
    ],
    nearby: ["pitman-nj", "glassboro-nj", "williamstown-nj", "deptford-nj"],
  },
  {
    slug: "williamstown-nj",
    city: "Williamstown",
    state: "NJ",
    county: "Gloucester County",
    title: "Williamstown NJ Limo & Airport Car Service",
    description:
      "Limo and airport car service in Williamstown and Monroe Township, NJ. Transfers to PHL, ACY, EWR and JFK, plus weddings and group events. (929) 452-6031.",
    h1: "Limo Service in Williamstown, NJ",
    tagline:
      "Chauffeured airport transfers and event rides for Monroe Township and the Route 322 corridor.",
    intro: [
      "Williamstown sits between Philadelphia and Atlantic City, which makes it a practical starting point for either airport. Route 42 and the Atlantic City Expressway are close, and our chauffeurs plan around the expressway traffic that builds on summer weekends.",
      "We serve Williamstown, Monroe Township and the surrounding rural neighborhoods with sedans, SUVs and Sprinters. Whether it is a wedding party, a golf outing or a 5 a.m. flight, the trip is confirmed before the day.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 35–45 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 40–50 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 55–70 min" },
      { code: "JFK", label: "JFK International", time: "About 85–100 min" },
    ],
    landmarks: [
      "Route 322 corridor",
      "Scotland Run Golf Club",
      "Monroe Township municipal area",
      "Atlantic City Expressway access",
      "Route 42 connection",
      "Local farm markets",
    ],
    neighborhoods: [
      "Williamstown",
      "Monroe Township",
      "Cecil",
      "Downer",
      "Franklin Township border",
      "Buena Vista border",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "Two-airport access: PHL and ACY are both reasonable runs from Williamstown.",
      },
      {
        label: "Golf and group outings",
        path: "/services/group-events",
        blurb:
          "Sprinter and group vehicles for golf trips, family reunions and club outings.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Wedding transportation for Monroe Township venues, farms and banquet halls.",
      },
      {
        label: "Wine and sightseeing",
        path: "/services/wine-and-sightseeing",
        blurb:
          "A chauffeured day through South Jersey wineries and countryside stops.",
      },
    ],
    faqs: [
      {
        q: "Do you serve all of Monroe Township?",
        a: "Yes. Williamstown, Cecil, Downer and the surrounding Monroe Township neighborhoods are covered.",
      },
      {
        q: "How long is Williamstown to Atlantic City Airport?",
        a: "Typically 40 to 50 minutes depending on Route 322 and expressway traffic.",
      },
      {
        q: "Can you take us to PHL instead of ACY?",
        a: "Yes. PHL is usually 35 to 45 minutes. We will confirm which airport and terminal your airline uses before the trip.",
      },
      {
        q: "Do you provide golf outing transportation?",
        a: "Yes. We can move a group to Scotland Run or other courses and arrange a return time, so nobody is designated driver.",
      },
    ],
    nearby: ["sewell-washington-township-nj", "sicklerville-winslow-nj", "vineland-nj", "glassboro-nj"],
  },
  {
    slug: "deptford-nj",
    city: "Deptford",
    state: "NJ",
    county: "Gloucester County",
    title: "Deptford NJ Limo Service & Airport Transfers",
    description:
      "Limo and airport car service in Deptford, NJ. Chauffeured rides to PHL, EWR and JFK, mall pickups, proms and corporate travel. Call (929) 452-6031.",
    h1: "Limo Service in Deptford, NJ",
    tagline:
      "Chauffeur-driven sedans, SUVs and Sprinters for Deptford Mall, Route 42 and PHL.",
    intro: [
      "Deptford is a retail and commuter hub for Gloucester County. The Deptford Mall and Clements Bridge Road draw shoppers from across the region, and Route 42 carries most of the traffic toward Philadelphia and the shore.",
      "We handle the trips that are easier without a car: airport transfers, prom groups, wedding parties and corporate visits. Our chauffeurs confirm the exact pickup point, which matters in busy mall lots and shopping centers.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 20–30 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 45–60 min" },
      { code: "JFK", label: "JFK International", time: "About 75–90 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 50–60 min" },
    ],
    landmarks: [
      "Deptford Mall",
      "Clements Bridge Road corridor",
      "Route 42 and Route 55 interchange",
      "Almonesson Lake",
      "Deptford Township municipal area",
      "Blackwood border",
    ],
    neighborhoods: [
      "Deptford Township",
      "Almonesson",
      "Jericho",
      "Westville Grove",
      "Woodbury Heights border",
      "Blackwood border",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "Short, reliable runs to PHL plus scheduled trips to EWR, JFK and ACY.",
      },
      {
        label: "Prom",
        path: "/services/prom",
        blurb:
          "Group transportation for Deptford-area proms with parent-confirmed timings.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Business travel and client pickups around the Route 42 commercial corridor.",
      },
      {
        label: "Group events",
        path: "/services/group-events",
        blurb:
          "Sprinter and group vehicle requests for parties, shopping trips and family events.",
      },
    ],
    faqs: [
      {
        q: "Can you pick up at Deptford Mall?",
        a: "Yes. Mall pickups are arranged at a specific entrance or curbside point so you are not searching the lot for your driver.",
      },
      {
        q: "How far is Deptford from PHL?",
        a: "About 20 to 30 minutes, depending on Route 42 and I-295 traffic.",
      },
      {
        q: "Do you handle large prom groups?",
        a: "Yes. We can arrange multiple pickups and a Sprinter or SUV based on group size, with return times confirmed with parents.",
      },
      {
        q: "Do you offer hourly service in Deptford?",
        a: "Yes. Hourly service works well for shopping trips, multiple appointments or a full wedding day.",
      },
    ],
    nearby: ["woodbury-nj", "west-deptford-nj", "sewell-washington-township-nj", "glassboro-nj"],
  },
  {
    slug: "cherry-hill-nj",
    city: "Cherry Hill",
    state: "NJ",
    county: "Camden County",
    title: "Cherry Hill Limo Service & Airport Car Service",
    description:
      "Limo and black car service in Cherry Hill, NJ. Airport transfers to PHL, EWR and JFK, corporate travel, weddings and event rides. Call (929) 452-6031.",
    h1: "Limo Service in Cherry Hill, NJ",
    tagline:
      "Professional chauffeurs for Cherry Hill's corporate offices, weddings and three-airport access.",
    intro: [
      "Cherry Hill is a business and retail center with direct access to I-295, Route 70 and Route 38, and it sits close enough to PHL that a chauffeured airport transfer is a realistic habit rather than a splurge. We serve Barclay, Erlton, Kingston Estates, Ashland and the rest of the township.",
      "For corporate clients, we handle point-to-point rides, client pickups and roadshows. For celebrations, we keep the bridal party and guests together and confirm every pickup time in advance.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 20–30 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 60–75 min" },
      { code: "JFK", label: "JFK International", time: "About 90–110 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 55–65 min" },
    ],
    landmarks: [
      "Cherry Hill Mall",
      "Garden State Park",
      "Barclay Farmstead",
      "Croft Farm",
      "Cooper River Park nearby",
      "Route 70 and I-295 corridors",
    ],
    neighborhoods: [
      "Barclay",
      "Erlton",
      "Kingston Estates",
      "Ashland",
      "Woodcrest",
      "Ellisburg",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "PHL in about half an hour, plus scheduled service to EWR, JFK and ACY for international trips.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Executive transportation for Cherry Hill offices, client visits and Philadelphia meetings.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Bridal party and guest shuttles for Cherry Hill and Cooper River-area venues.",
      },
      {
        label: "Hourly service",
        path: "/services/hourly-as-directed",
        blurb:
          "A chauffeur on standby for multi-stop days, shopping trips or a full evening out.",
      },
    ],
    faqs: [
      {
        q: "Can you pick up at Cherry Hill Mall or Garden State Park?",
        a: "Yes. We confirm a specific entrance or curbside point so the pickup is quick, even on busy weekends.",
      },
      {
        q: "How long does Cherry Hill to PHL take?",
        a: "Usually 20 to 30 minutes via I-295 or Route 70 and I-95, depending on traffic.",
      },
      {
        q: "Do you provide transportation to JFK and Newark?",
        a: "Yes. Both airports are scheduled regularly from Cherry Hill, with pickup times planned around your flight.",
      },
      {
        q: "Do you handle corporate accounts in Cherry Hill?",
        a: "Yes. We arrange recurring executive travel, client pickups and event transportation for local businesses.",
      },
    ],
    nearby: ["voorhees-nj", "berlin-nj", "philadelphia-pa", "deptford-nj"],
  },
  {
    slug: "voorhees-nj",
    city: "Voorhees",
    state: "NJ",
    county: "Camden County",
    title: "Voorhees NJ Limo Service & Airport Transfers",
    description:
      "Limo and airport car service in Voorhees, NJ. Chauffeured transfers to PHL, EWR and JFK, hospital and corporate travel, weddings. Call (929) 452-6031.",
    h1: "Limo Service in Voorhees, NJ",
    tagline:
      "Chauffeur-driven rides for Echelon, Ashland and Kirkwood — airports, hospitals and events.",
    intro: [
      "Voorhees combines a large medical and corporate presence with easy access to Route 73 and I-295. That mix produces a lot of scheduled travel: hospital visits, office meetings, client pickups and flights out of PHL or Newark.",
      "We serve Echelon, Ashland, Kirkwood, Alluvium and the rest of the township. Every ride is private and scheduled, and our chauffeurs confirm pickup details before the day so there is no guessing at a busy entrance.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 25–35 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 60–75 min" },
      { code: "JFK", label: "JFK International", time: "About 90–110 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 50–60 min" },
    ],
    landmarks: [
      "Voorhees Town Center",
      "Virtua Voorhees Hospital",
      "Connolly Park",
      "Kirkwood Lake",
      "Route 73 corridor",
      "Echelon corporate area",
    ],
    neighborhoods: [
      "Echelon",
      "Ashland",
      "Kirkwood",
      "Alluvium",
      "Kresson",
      "Glendale",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "Scheduled airport service to PHL, EWR, JFK and ACY with flight tracking and buffer time.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Private transportation for Echelon offices, client visits and Philadelphia meetings.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Bridal party and guest transportation for Voorhees venues and nearby country clubs.",
      },
      {
        label: "Group events",
        path: "/services/group-events",
        blurb:
          "Sprinter and group vehicles for family events, tournaments and nights out.",
      },
    ],
    faqs: [
      {
        q: "Do you provide rides to Virtua Voorhees Hospital?",
        a: "Yes. We schedule private car service to appointments and discharges. We are not a medical transport provider, so please confirm any clinical needs with the hospital.",
      },
      {
        q: "How long is Voorhees to PHL?",
        a: "About 25 to 35 minutes depending on Route 73 and I-295 traffic.",
      },
      {
        q: "Can you pick up at Voorhees Town Center?",
        a: "Yes. We agree on a specific entrance or curbside point before the pickup.",
      },
      {
        q: "Do you offer corporate accounts in Echelon?",
        a: "Yes. We handle recurring business travel and client transportation for Voorhees-area companies.",
      },
    ],
    nearby: ["cherry-hill-nj", "berlin-nj", "sicklerville-winslow-nj", "philadelphia-pa"],
  },
  {
    slug: "sicklerville-winslow-nj",
    city: "Sicklerville",
    state: "NJ",
    county: "Camden County",
    title: "Sicklerville & Winslow Limo Service",
    description:
      "Limo and airport car service in Sicklerville and Winslow Township, NJ. Transfers to ACY, PHL, EWR and JFK, plus weddings and group rides. (929) 452-6031.",
    h1: "Limo Service in Sicklerville & Winslow Township, NJ",
    tagline:
      "Chauffeured rides between the Atlantic City Expressway and Philadelphia — airports, proms and weddings.",
    intro: [
      "Sicklerville sits in Winslow Township, close to Route 42 and the Atlantic City Expressway. That location makes both ACY and PHL practical airport choices, and it puts shore trips and Philadelphia events within easy reach.",
      "We serve Sicklerville, Blue Anchor, Tansboro, Cedar Brook and the surrounding Winslow neighborhoods. For proms, weddings and family events, our chauffeurs handle multiple pickups and a confirmed return time.",
    ],
    airports: [
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 40–50 min" },
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 30–40 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 55–70 min" },
      { code: "JFK", label: "JFK International", time: "About 85–100 min" },
    ],
    landmarks: [
      "Atlantic City Expressway access",
      "Route 42 corridor",
      "Sicklerville Road",
      "Winslow Township municipal area",
      "Blue Anchor",
      "Atco border",
    ],
    neighborhoods: [
      "Sicklerville",
      "Winslow Township",
      "Blue Anchor",
      "Tansboro",
      "Cedar Brook",
      "Atco border",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "Convenient ACY and PHL transfers, plus scheduled Newark and JFK service.",
      },
      {
        label: "Prom",
        path: "/services/prom",
        blurb:
          "Winslow-area prom groups with multiple pickups and a parent-confirmed return.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Bridal party and guest transportation for Sicklerville and Winslow venues.",
      },
      {
        label: "Group events",
        path: "/services/group-events",
        blurb:
          "Sprinters and group vehicles for shore trips, parties and family events.",
      },
    ],
    faqs: [
      {
        q: "Do you cover all of Winslow Township?",
        a: "Yes. Sicklerville, Blue Anchor, Tansboro, Cedar Brook and the surrounding area are covered.",
      },
      {
        q: "How far is Sicklerville from Atlantic City Airport?",
        a: "Typically 40 to 50 minutes via Route 42 and the expressway, depending on traffic.",
      },
      {
        q: "Can you take a group to the shore?",
        a: "Yes. Ocean City, Atlantic City, Wildwood and Cape May trips are available with a confirmed return time.",
      },
      {
        q: "Do you provide prom transportation in Winslow?",
        a: "Yes. We coordinate group pickups, photo stops and the return schedule with parents in advance.",
      },
    ],
    nearby: ["williamstown-nj", "berlin-nj", "voorhees-nj", "vineland-nj"],
  },
  {
    slug: "berlin-nj",
    city: "Berlin",
    state: "NJ",
    county: "Camden County",
    title: "Berlin NJ Limo Service & Airport Car Service",
    description:
      "Limo and airport car service in Berlin and Berlin Township, NJ. Chauffeured transfers to PHL, ACY and EWR, weddings and group events. (929) 452-6031.",
    h1: "Limo Service in Berlin, NJ",
    tagline:
      "Chauffeured rides for Berlin borough and township, halfway between Philadelphia and the shore.",
    intro: [
      "Berlin sits on Route 73 between Philadelphia and the shore points, with the Atlantic City Expressway close by. It is a practical base for both PHL and ACY airport runs, and a convenient starting point for a night in Philadelphia.",
      "We serve Berlin borough, Berlin Township and the surrounding neighborhoods with sedans, SUVs and Sprinters, plus hourly service when a day has several stops.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 30–40 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 40–50 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 55–70 min" },
      { code: "JFK", label: "JFK International", time: "About 85–100 min" },
    ],
    landmarks: [
      "Route 73 corridor",
      "Berlin borough center",
      "Atlantic City Expressway access",
      "Berlin Township municipal area",
      "Local farm markets",
      "Nearby Berlin-Cross Keys area",
    ],
    neighborhoods: [
      "Berlin borough",
      "Berlin Township",
      "West Berlin",
      "Cross Keys",
      "Voorhees border",
      "Waterford border",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "Scheduled PHL, ACY, EWR and JFK service with flight tracking and luggage-aware vehicles.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Bridal party and guest transportation for Berlin and Camden County venues.",
      },
      {
        label: "Night on the town",
        path: "/services/night-on-the-town",
        blurb:
          "Philadelphia dining, concerts and games with a scheduled ride home.",
      },
      {
        label: "Hourly service",
        path: "/services/hourly-as-directed",
        blurb:
          "A vehicle on standby for multi-stop errands, appointments or a full event day.",
      },
    ],
    faqs: [
      {
        q: "Do you serve both Berlin borough and Berlin Township?",
        a: "Yes. Both are covered, along with West Berlin and the surrounding neighborhoods.",
      },
      {
        q: "How long is Berlin to PHL?",
        a: "Usually 30 to 40 minutes via Route 73 and I-295, depending on traffic.",
      },
      {
        q: "Can you take us to Atlantic City Airport?",
        a: "Yes. ACY is typically 40 to 50 minutes from Berlin via the expressway.",
      },
      {
        q: "Do you offer hourly service for events?",
        a: "Yes. Hourly and as-directed service keeps the vehicle with you between stops.",
      },
    ],
    nearby: ["sicklerville-winslow-nj", "voorhees-nj", "cherry-hill-nj", "williamstown-nj"],
  },
  {
    slug: "vineland-nj",
    city: "Vineland",
    state: "NJ",
    county: "Cumberland County",
    title: "Vineland NJ Limo Service & Airport Car Service",
    description:
      "Limo and airport car service in Vineland, NJ and Cumberland County. Transfers to ACY and PHL, weddings, corporate rides and events. Call (929) 452-6031.",
    h1: "Limo Service in Vineland, NJ",
    tagline:
      "Chauffeured airport transfers and event rides across Cumberland County.",
    intro: [
      "Vineland is the largest city in Cumberland County and a short run from Atlantic City International Airport. Landis Avenue and Delsea Drive carry most local traffic, and Route 55 connects the city to Gloucester County and Philadelphia.",
      "We serve Vineland and the surrounding Cumberland County communities with scheduled airport transfers, wedding transportation and corporate rides. For long airport days, the chauffeur handles the drive in both directions.",
    ],
    airports: [
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 30–40 min" },
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 45–60 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 75–90 min" },
      { code: "JFK", label: "JFK International", time: "About 105–125 min" },
    ],
    landmarks: [
      "Landis Avenue business district",
      "Cumberland Mall",
      "Rowan College of South Jersey Cumberland campus",
      "Vineland Historical and Antiquarian Society",
      "Delsea Drive (Route 47)",
      "Route 55 connection",
    ],
    neighborhoods: [
      "Vineland",
      "East Vineland",
      "North Vineland",
      "Landis Avenue corridor",
      "Millville border",
      "Newfield border",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "Short ACY runs plus scheduled PHL, Newark and JFK service for longer trips.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Bridal party and guest transportation for Vineland and Cumberland County venues.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Business travel and client pickups along Landis Avenue and Route 55.",
      },
      {
        label: "Group events",
        path: "/services/group-events",
        blurb:
          "Sprinter and group vehicles for family events, tournaments and shore trips.",
      },
    ],
    faqs: [
      {
        q: "How far is Vineland from Atlantic City Airport?",
        a: "Typically 30 to 40 minutes depending on Route 322 and expressway traffic.",
      },
      {
        q: "Do you serve the rest of Cumberland County?",
        a: "Yes. Millville, Newfield and nearby Cumberland County communities are within our service area.",
      },
      {
        q: "How long does Vineland to PHL take?",
        a: "About 45 to 60 minutes via Route 55 and I-295, depending on traffic.",
      },
      {
        q: "Can you pick up on Landis Avenue?",
        a: "Yes. We confirm a precise curbside point with you before the trip.",
      },
    ],
    nearby: ["williamstown-nj", "sicklerville-winslow-nj", "glassboro-nj", "mullica-hill-nj"],
  },
  {
    slug: "philadelphia-pa",
    city: "Philadelphia",
    state: "PA",
    county: "Philadelphia County",
    title: "Philadelphia Limo Service & Airport Car Service",
    description:
      "Chauffeured limo and black car service in Philadelphia. Center City to PHL, EWR and JFK transfers, weddings, corporate travel and events. (929) 452-6031.",
    h1: "Limo Service in Philadelphia, PA",
    tagline:
      "Center City pickups, airport transfers and event transportation from a South Jersey chauffeur team.",
    intro: [
      "Philadelphia is our largest out-of-state market. We run Center City, Old City, Rittenhouse, University City, Fairmount, Fishtown and South Philly pickups for airport transfers, corporate travel, weddings and nights out.",
      "Because our base is in Glassboro, we cross the Walt Whitman, Ben Franklin and Commodore Barry bridges daily. Bridge traffic and tolls are part of the plan, and your quote covers the details before the trip, so there are no surprises on the day.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 15–25 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 60–80 min" },
      { code: "JFK", label: "JFK International", time: "About 90–120 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 55–70 min" },
    ],
    landmarks: [
      "Center City and Rittenhouse Square",
      "Old City and Independence Hall",
      "University City and 30th Street Station",
      "Fairmount and the Museum District",
      "Fishtown and Northern Liberties",
      "South Philadelphia Sports Complex",
    ],
    neighborhoods: [
      "Center City",
      "Old City",
      "Rittenhouse Square",
      "University City",
      "Fishtown",
      "South Philadelphia",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "PHL is minutes away; Newark and JFK transfers are scheduled with traffic buffer built in.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Executive and client transportation for Center City offices and University City institutions.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Bridal party and guest transportation for Philadelphia venues and hotels.",
      },
      {
        label: "Night on the town",
        path: "/services/night-on-the-town",
        blurb:
          "Restaurants, concerts and games with a chauffeur handling traffic, tolls and the ride home.",
      },
    ],
    faqs: [
      {
        q: "Do you pick up in Center City and Old City?",
        a: "Yes. We serve Center City, Old City, Rittenhouse, University City, Fishtown, South Philly and other Philadelphia neighborhoods.",
      },
      {
        q: "How long does Center City to PHL take?",
        a: "Usually 15 to 25 minutes, though I-95 and bridge traffic can add time. We build a buffer around your flight.",
      },
      {
        q: "Are bridge tolls included in the quote?",
        a: "Tolls and any required parking are confirmed with your quote so the final price is clear before you travel.",
      },
      {
        q: "Do you drive from Philadelphia to New Jersey airports?",
        a: "Yes. EWR and JFK transfers from Philadelphia are routine, with pickup times planned around your flight.",
      },
    ],
    nearby: ["cherry-hill-nj", "voorhees-nj", "west-deptford-nj", "glassboro-nj"],
  },
];

// Wider NJ, Pennsylvania and New York coverage lives in ./areas/ so this file
// stays focused on the South Jersey home market.
export const serviceAreas: ServiceArea[] = [
  ...southJerseyAreas,
  ...njAreas,
  ...paAreas,
  ...nyAreas,
];

export const serviceAreaSlugs: string[] = serviceAreas.map((a) => a.slug);

export const getServiceArea = (slug?: string): ServiceArea | undefined =>
  serviceAreas.find((a) => a.slug === slug);
